import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import Layout from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import AuthImage from "@/components/AuthImage";
import EditPostDialog from "@/components/EditPostDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, ChevronRight, Send, Check, AlertTriangle, X, MessageSquare, ArrowLeft, Trash2, ImagePlus, ArrowUp, ArrowDown, GripVertical } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const NETWORK_LABEL = { instagram: "Instagram", facebook: "Facebook", linkedin: "LinkedIn", tiktok: "TikTok" };
const TYPE_LABEL = { feed: "Feed", story: "Story", reels: "Reels" };

export default function PostDetail() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [imageIdx, setImageIdx] = useState(0);
  const [sending, setSending] = useState(false);
  const [editImagesOpen, setEditImagesOpen] = useState(false);
  const [editPostOpen, setEditPostOpen] = useState(false);
  const [siblings, setSiblings] = useState([]);
  const commentsEndRef = useRef(null);
  const skipScrollRef = useRef(true);

  const isStaff = user?.role === "admin" || user?.role === "staff" || user?.role === "designer";
  const isAdmin = user?.role === "admin";
  const isClient = user?.role === "client";

  const load = async () => {
    try {
      const [p, c] = await Promise.all([
        api.get(`/posts/${postId}`),
        api.get(`/posts/${postId}/comments`),
      ]);
      setPost(p.data);
      setComments(c.data);
      setImageIdx(0);
      // Load siblings of the same client + month for prev/next navigation
      try {
        const s = await api.get("/posts", {
          params: { client_id: p.data.client_id, year: p.data.year, month: p.data.month },
        });
        const sorted = [...s.data].sort((a, b) => {
          const da = a.day || 0, db = b.day || 0;
          if (da !== db) return da - db;
          return (a.scheduled_time || "").localeCompare(b.scheduled_time || "");
        });
        setSiblings(sorted);
      } catch { setSiblings([]); }
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao carregar post");
      navigate(-1);
    }
  };

  const currentIndex = siblings.findIndex((s) => s.id === postId);
  const prevSibling = currentIndex > 0 ? siblings[currentIndex - 1] : null;
  const nextSibling = currentIndex >= 0 && currentIndex < siblings.length - 1 ? siblings[currentIndex + 1] : null;

  useEffect(() => {
    skipScrollRef.current = true; // skip the comments-scroll effect that fires when new post loads
    load();
    window.scrollTo({ top: 0, behavior: "auto" });
    /* eslint-disable-next-line */
  }, [postId]);

  useEffect(() => {
    if (skipScrollRef.current) {
      skipScrollRef.current = false;
      return;
    }
    commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [comments]);

  useEffect(() => {
    const onKey = (e) => {
      // Ignore when typing in inputs/textareas
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.key === "ArrowLeft" && prevSibling) navigate(`/post/${prevSibling.id}`);
      if (e.key === "ArrowRight" && nextSibling) navigate(`/post/${nextSibling.id}`);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prevSibling, nextSibling, navigate]);

  const updateStatus = async (status) => {
    try {
      await api.put(`/posts/${postId}/status`, { status });
      toast.success("Estado atualizado");
      load();
    } catch {
      toast.error("Erro ao atualizar estado");
    }
  };

  const reorderImages = async (fromIdx, toIdx) => {
    if (!post || fromIdx === toIdx) return;
    const imgs = [...(post.images || [])];
    if (toIdx < 0 || toIdx >= imgs.length) return;
    const [moved] = imgs.splice(fromIdx, 1);
    imgs.splice(toIdx, 0, moved);
    try {
      await api.put(`/posts/${postId}`, { images: imgs });
      setImageIdx(toIdx);
      toast.success("Ordem atualizada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao reordenar");
    }
  };

  const sendComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setSending(true);
    try {
      await api.post(`/posts/${postId}/comments`, { content: newComment });
      setNewComment("");
      const c = await api.get(`/posts/${postId}/comments`);
      setComments(c.data);
    } catch {
      toast.error("Erro ao enviar comentário");
    } finally {
      setSending(false);
    }
  };

  const goToClientPlan = () => {
    if (!post) { navigate(-1); return; }
    navigate(`/cliente/${post.client_id}?year=${post.year}&month=${post.month}`);
  };

  const deletePost = async () => {
    if (!window.confirm("Eliminar este post permanentemente?")) return;
    try {
      await api.delete(`/posts/${postId}`);
      toast.success("Post eliminado");
      goToClientPlan();
    } catch {
      toast.error("Erro ao eliminar");
    }
  };
  if (!post) {
    return (
      <Layout>
        <div className="text-center text-[#5C4A43] py-20 text-xs uppercase tracking-widest">A carregar…</div>
      </Layout>
    );
  }

  const images = post.images || [];

  return (
    <Layout>
      <div className="mb-6 sm:mb-8 flex items-center justify-between gap-2 flex-wrap">
        <button
          onClick={goToClientPlan}
          className="flex items-center gap-2 text-[10px] sm:text-xs text-[#5C4A43] hover:text-[#3B2B23] transition-colors uppercase tracking-widest"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
        {siblings.length > 1 && (
          <div className="flex items-center gap-1 text-[10px] sm:text-xs text-[#5C4A43] uppercase tracking-widest" data-testid="post-nav">
            <button
              onClick={() => prevSibling && navigate(`/post/${prevSibling.id}`)}
              disabled={!prevSibling}
              className="flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-full hover:bg-[#EAE3DB] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="prev-post-button"
              title={prevSibling ? `Dia ${String(prevSibling.day).padStart(2, "0")}` : "Sem posts anteriores"}
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Anterior</span>
            </button>
            <span className="px-1 text-[#8B5E3C]">{currentIndex + 1} / {siblings.length}</span>
            <button
              onClick={() => nextSibling && navigate(`/post/${nextSibling.id}`)}
              disabled={!nextSibling}
              className="flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-full hover:bg-[#EAE3DB] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="next-post-button"
              title={nextSibling ? `Dia ${String(nextSibling.day).padStart(2, "0")}` : "Sem posts seguintes"}
            >
              <span className="hidden sm:inline">Seguinte</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          {isStaff && (
            <button
              onClick={() => setEditPostOpen(true)}
              className="text-[10px] sm:text-xs text-[#3B2B23] hover:bg-[#EAE3DB] px-3 py-1.5 rounded-full transition-colors flex items-center gap-2 uppercase tracking-widest"
              data-testid="edit-post-button"
            >
              <ImagePlus className="w-4 h-4" /> Editar<span className="hidden sm:inline"> post</span>
            </button>
          )}
          {isStaff && (
            <button
              onClick={deletePost}
              className="text-[10px] sm:text-xs text-[#8A2A22] hover:bg-[#FBE6E4] px-3 py-1.5 rounded-full transition-colors flex items-center gap-2 uppercase tracking-widest"
              data-testid="delete-post-button"
            >
              <Trash2 className="w-4 h-4" /> Eliminar<span className="hidden sm:inline"> post</span>
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10">
        {/* Image carousel */}
        <div>
          <div className="card-soft overflow-hidden">
            <div className="bg-white relative flex items-center justify-center min-h-[280px] max-h-[80vh]">
              {images.length > 0 ? (
                <>
                  {images[imageIdx]?.media_type === "video" ? (
                    <video
                      key={images[imageIdx].path}
                      src={fileUrl(images[imageIdx].path)}
                      controls
                      className="max-w-full max-h-[80vh] w-auto h-auto object-contain bg-black"
                      data-testid="carousel-video"
                    />
                  ) : (
                    <AuthImage
                      path={images[imageIdx]?.path}
                      className="max-w-full max-h-[80vh] w-auto h-auto object-contain"
                    />
                  )}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={() => setImageIdx((i) => (i - 1 + images.length) % images.length)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-[#3B2B23] rounded-full w-9 h-9 flex items-center justify-center shadow-sm"
                        data-testid="carousel-prev"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setImageIdx((i) => (i + 1) % images.length)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-[#3B2B23] rounded-full w-9 h-9 flex items-center justify-center shadow-sm"
                        data-testid="carousel-next"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {images.map((_, i) => (
                          <span
                            key={i}
                            className={`w-1.5 h-1.5 rounded-full ${i === imageIdx ? "bg-white" : "bg-white/50"}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full aspect-square flex items-center justify-center text-[#5C4A43] text-3xl font-bold">
                  Sem imagens
                </div>
              )}
            </div>
          </div>

          {images.length > 1 && (
            <div className="mt-3">
              {(isAdmin || isStaff) && (
                <div className="text-[10px] uppercase tracking-widest text-[#8B5E3C] mb-2">
                  Ordem do carrossel · arrasta ou usa as setas
                </div>
              )}
              <div className="flex gap-2 overflow-x-auto pb-2" data-testid="carousel-thumbs">
                {images.map((img, i) => (
                  <div
                    key={`${img.path}-${i}`}
                    draggable={isAdmin || isStaff}
                    onDragStart={(e) => e.dataTransfer.setData("text/plain", String(i))}
                    onDragOver={(e) => { if (isAdmin || isStaff) e.preventDefault(); }}
                    onDrop={(e) => {
                      if (!(isAdmin || isStaff)) return;
                      e.preventDefault();
                      const from = parseInt(e.dataTransfer.getData("text/plain"), 10);
                      if (Number.isNaN(from)) return;
                      reorderImages(from, i);
                    }}
                    className={`relative group flex-shrink-0 ${i === imageIdx ? "" : "opacity-70"}`}
                    data-testid={`carousel-thumb-${i}`}
                  >
                    <button
                      onClick={() => setImageIdx(i)}
                      className={`w-16 h-16 rounded-lg overflow-hidden border-2 block ${
                        i === imageIdx ? "border-[#3B2B23]" : "border-transparent"
                      }`}
                    >
                      {img.media_type === "video" ? (
                        <div className="w-full h-full bg-[#3B2B23] flex items-center justify-center">
                          <span className="text-[#F9F6F0] text-xs">▶</span>
                        </div>
                      ) : (
                        <AuthImage path={img.path} className="w-full h-full object-cover" />
                      )}
                    </button>
                    <span className="absolute top-0.5 left-0.5 bg-[#3B2B23] text-[#F9F6F0] rounded-full w-4 h-4 text-[9px] flex items-center justify-center font-bold">
                      {i + 1}
                    </span>
                    {(isAdmin || isStaff) && (
                      <>
                        <span className="absolute top-0.5 right-0.5 hidden group-hover:flex items-center justify-center w-4 h-4 rounded-full bg-black/40 text-white">
                          <GripVertical className="w-3 h-3" />
                        </span>
                        <div className="mt-1 flex gap-0.5 justify-center">
                          <button
                            onClick={() => reorderImages(i, i - 1)}
                            disabled={i === 0}
                            className="p-0.5 rounded hover:bg-[#EAE3DB] text-[#3B2B23] disabled:opacity-30 disabled:cursor-not-allowed"
                            data-testid={`move-up-${i}`}
                            aria-label="Mover para trás"
                          >
                            <ArrowUp className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => reorderImages(i, i + 1)}
                            disabled={i === images.length - 1}
                            className="p-0.5 rounded hover:bg-[#EAE3DB] text-[#3B2B23] disabled:opacity-30 disabled:cursor-not-allowed"
                            data-testid={`move-down-${i}`}
                            aria-label="Mover para a frente"
                          >
                            <ArrowDown className="w-3 h-3" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Details */}
        <div className="space-y-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B5E3C] mb-3">
              {(post.social_networks || [post.social_network]).filter(Boolean).map(n => NETWORK_LABEL[n] || n).join(" · ")} · {TYPE_LABEL[post.post_type]}
            </div>
            <h1 className="text-3xl sm:text-4xl text-[#1A1412]">
              Dia {String(post.day).padStart(2, "0")} · {String(post.month).padStart(2, "0")}/{post.year}
              {post.scheduled_time && (
                <span className="text-[#8B5E3C] italic text-2xl font-light"> · {post.scheduled_time}</span>
              )}
            </h1>
            <div className="mt-3">
              <StatusBadge status={post.status} />
            </div>
          </div>

          {/* Copy */}
          <Card className="card-soft p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B5E3C] mb-3">A minha copy</div>
            {post.copy ? (
              <p className="notranslate text-[#1A1412] whitespace-pre-wrap leading-relaxed text-sm font-light" translate="no">
                {post.copy}
              </p>
            ) : (
              <p className="text-[#8B5E3C] italic text-sm">Sem copy definida.</p>
            )}
          </Card>

          {/* Approval actions */}
          {(isClient || isAdmin || isStaff) && (
            <Card className="card-soft p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B5E3C] mb-4">
                {isClient ? "A minha decisão" : "Estado do post"}
              </div>
              {(isClient || isAdmin || isStaff) && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Button
                    onClick={() => updateStatus("approved")}
                    className="rounded-full bg-[#E1EDE4] text-[#2C543B] hover:bg-[#C9DDD0] h-11 text-xs uppercase tracking-widest"
                    data-testid="approve-button"
                  >
                    <Check className="w-4 h-4 mr-2" /> Aprovo
                  </Button>
                  <Button
                    onClick={() => updateStatus("changes_requested")}
                    className="rounded-full bg-[#FDF3E1] text-[#805B10] hover:bg-[#F5E5C8] h-11 text-xs uppercase tracking-widest"
                    data-testid="changes-button"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" /> Alterações
                  </Button>
                  <Button
                    onClick={() => updateStatus("rejected")}
                    className="rounded-full bg-[#FBE6E4] text-[#8A2A22] hover:bg-[#F3D2CE] h-11 text-xs uppercase tracking-widest"
                    data-testid="reject-button"
                  >
                    <X className="w-4 h-4 mr-2" /> Não aprovo
                  </Button>
                </div>
              )}
              {isStaff && (
                <div className="mt-3 pt-3 border-t border-[#EAE3DB] space-y-2">
                  {post.status === "published" ? (
                    <Button
                      onClick={() => updateStatus("approved")}
                      className="w-full rounded-full bg-[#EAE3DB] text-[#3B2B23] hover:bg-[#D9CFC2] h-11 text-xs uppercase tracking-widest"
                      data-testid="unpublish-button"
                    >
                      <X className="w-4 h-4 mr-2" /> Desmarcar publicado
                    </Button>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {post.status === "scheduled" ? (
                        <Button
                          onClick={() => updateStatus("approved")}
                          className="rounded-full bg-[#EAE3DB] text-[#3B2B23] hover:bg-[#D9CFC2] h-11 text-xs uppercase tracking-widest"
                          data-testid="unschedule-button"
                        >
                          <X className="w-4 h-4 mr-2" /> Desagendar
                        </Button>
                      ) : (
                        <Button
                          onClick={() => updateStatus("scheduled")}
                          className="rounded-full bg-[#1E4A75] text-[#F9F6F0] hover:bg-[#163459] h-11 text-xs uppercase tracking-widest"
                          data-testid="schedule-button"
                        >
                          <Check className="w-4 h-4 mr-2" /> Agendado
                        </Button>
                      )}
                      <Button
                        onClick={() => updateStatus("published")}
                        className="rounded-full bg-[#1E5A2C] text-[#F9F6F0] hover:bg-[#164421] h-11 text-xs uppercase tracking-widest"
                        data-testid="publish-button"
                      >
                        <Check className="w-4 h-4 mr-2" /> Marcar como publicado
                      </Button>
                    </div>
                  )}
                  <div className="text-[10px] text-[#8B5E3C] text-center uppercase tracking-widest">
                    Só a equipa
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>

      <EditPostDialog
        open={editPostOpen}
        onOpenChange={setEditPostOpen}
        post={post}
        onSaved={load}
      />

      {/* Comments thread */}
      <section className="mt-12">
        <h2 className="text-2xl sm:text-3xl text-[#1A1412] flex items-center gap-3 mb-6">
          <MessageSquare className="w-5 h-5 text-[#8B5E3C]" /> A minha conversa
        </h2>
        <Card className="card-soft p-6">
          <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2" data-testid="comments-list">
            {comments.length === 0 ? (
              <p className="text-[#8B5E3C] italic text-sm text-center py-6">
                Ainda não comentei nada. Posso ser o primeiro a deixar uma nota.
              </p>
            ) : (
              comments.map((c) => (
                <div
                  key={c.id}
                  className={`flex ${c.user_id === user.id ? "justify-end" : "justify-start"}`}
                  data-testid={`comment-${c.id}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      c.is_system
                        ? "bg-[#EAE3DB] text-[#5C4A43] italic text-xs"
                        : c.user_id === user.id
                          ? "bg-[#3B2B23] text-[#F9F6F0]"
                          : "bg-[#F9F6F0] border border-[#EAE3DB] text-[#1A1412]"
                    }`}
                  >
                    {!c.is_system && (
                      <div className={`flex items-center justify-between gap-3 text-[10px] mb-2 uppercase tracking-widest ${c.user_id === user.id ? "text-[#E6DED6]" : "text-[#8B5E3C]"}`}>
                        <span>{c.user_name} · {c.user_role === "admin" ? "Gestor" : c.user_role === "client" ? "Cliente" : "Equipa"}</span>
                        <span className={`font-mono normal-case tracking-normal ${c.user_id === user.id ? "text-[#E6DED6]/90" : "text-[#5C4A43]"}`}>
                          {new Date(c.created_at).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                    )}
                    <div className="notranslate text-sm whitespace-pre-wrap leading-relaxed font-light" translate="no">{c.content}</div>
                    {c.is_system && (
                      <div className="text-[10px] mt-1.5 font-mono opacity-80" data-testid={`comment-time-${c.id}`}>
                        {new Date(c.created_at).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
            <div ref={commentsEndRef} />
          </div>

          <form onSubmit={sendComment} className="mt-6 flex items-end gap-3">
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Escreva um comentário…"
              rows={2}
              className="flex-1 bg-white border-[#EAE3DB] resize-none"
              data-testid="comment-input"
            />
            <Button
              type="submit"
              disabled={sending || !newComment.trim()}
              className="rounded-full bg-[#3B2B23] hover:bg-[#2A1E18] text-[#F9F6F0] h-11 w-11 p-0 flex-shrink-0"
              data-testid="send-comment-button"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </Card>
      </section>
    </Layout>
  );
}
